import { gql } from '@urql/core';
import React, { useMemo } from 'react';
import { useAppState } from '../../../client/providers/AppProvider';
import { Espin_Role_Enum, PlayerInfoButtonsFragment } from '../../../generated/spin-graphql';
import { Tooltip } from '../../common/Tooltip';
import { EmailIcon } from '../../svg/EmailIcon';
import { PhoneIcon } from '../../svg/PhoneIcon';
import { DiscordIcon } from '../../svg/socials/DiscordIcon';

type Props = {
    player: PlayerInfoButtonsFragment;
    showPhone?: boolean;
};

gql`
    fragment PlayerInfoButtons on espin_player_profile {
        social {
            discord
        }
        user {
            email
            id
            subscription_status {
                is_subscribed
            }
        }
        phone
    }
`;

export const PlayerInfoButtons: React.FC<Props> = ({ player, showPhone = true }) => {
    const { subtier, user } = useAppState();
    const canSeeContactInformation = useMemo(() => {
        // is this myself looking at it - then yes
        if (user?.id === player.user.id) {
            return true;
        }
        // if this is my parent looking at it - then yes
        if (
            user?.role === Espin_Role_Enum.EspinParentUser &&
            !!user.parent_profile.child_profiles.find((c) => c.user_id === player.user.id)
        ) {
            return true;
        }

        // if this is a tier 1 college, and the player is also subscribed, then yes
        if (
            user?.role === Espin_Role_Enum.EspinCollegeUser &&
            subtier === 1 &&
            player.user.subscription_status?.is_subscribed
        ) {
            return true;
        }

        // if this is a tier 2 college, then yes
        if (user?.role === Espin_Role_Enum.EspinCollegeUser && subtier >= 2) {
            return true;
        }

        // else no
        return false;
    }, [subtier, player, user]);
    return (
        <div className="info-button-container">
            {player.social?.discord && (
                <>
                    {canSeeContactInformation && (
                        <p className="info-button discord">
                            <DiscordIcon />
                            {player.social.discord}
                        </p>
                    )}
                    {!canSeeContactInformation && (
                        <Tooltip
                            buttonLink="/dashboard/subscriptions"
                            buttonLabel="Upgrade"
                            message="This feature is only available to Tier 2 subscribers."
                            customButton={<DiscordIcon />}
                        />
                    )}
                </>
            )}
            {player.user.email && (
                <>
                    {canSeeContactInformation && (
                        <a href={`mailto:${player.user.email}`} className="info-button">
                            {player.user.email}
                        </a>
                    )}
                    {!canSeeContactInformation && (
                        <Tooltip
                            buttonLink="/dashboard/subscriptions"
                            buttonLabel="Upgrade"
                            message="This feature is only available to Tier 2 subscribers."
                            customButton={<EmailIcon />}
                        />
                    )}
                </>
            )}
            {showPhone && player.phone && (
                <>
                    {canSeeContactInformation && (
                        <a href={`tel:${player.phone}`} className="info-button">
                            <PhoneIcon />
                            {player.phone}
                        </a>
                    )}
                    {!canSeeContactInformation && (
                        <Tooltip
                            buttonLink="/dashboard/subscriptions"
                            buttonLabel="Upgrade"
                            message="This feature is only available to Tier 2 subscribers."
                            customButton={<PhoneIcon />}
                        />
                    )}
                </>
            )}
        </div>
    );
};
